import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Input,
  Divider,
  InputAdornment,
  Pagination,
  useTheme,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
  Chip
} from '@mui/material';
import { BglPrimaryButton, BglPrimaryOutlinedButton, BglPrimaryOutlinedSmallButton } from 'components/button/BglButtons';
import { BglOverlayLoader } from 'components/loading';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId ,selectPilotList} from 'store/dataReducers/pilotsSlice';
import { PilotApis } from 'apis/pilotAPI';
import { ClusterApis } from 'apis/clusterAPI';
import { IngestionConfig } from 'types/apis/pilotInterfaces';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
// removed autosize import; using TextField multiline instead
import { BglSelect } from 'components/common/BglSelect';

const ROWS_PER_PAGE = 10;

export const PilotConfigsPage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  
  const [configs, setConfigs] = useState<IngestionConfig[]>([]);
  const [search, setSearch] = useState('');
  const [lastSaved, setLastSaved] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Inline editing states (Studio-like UX)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [savedHighlightIds, setSavedHighlightIds] = useState<string[]>([]);
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});

  // Override dialog states (Studio-like Action column)
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideConfig, setOverrideConfig] = useState<IngestionConfig | null>(null);
  const [overrideLevel, setOverrideLevel] = useState<'pilot' | 'cluster'>('pilot');
  const [overrideCluster, setOverrideCluster] = useState<string>('');
  const [overrideValue, setOverrideValue] = useState<string>('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Filter states
  const [levelFilter, setLevelFilter] = useState('pilot');
  const [applicationFilter, setApplicationFilter] = useState('');
  const [fuelTypeFilter, setFuelTypeFilter] = useState('All');
  const [restrictedFilter, setRestrictedFilter] = useState('');

  // Cluster related states
  const [projects, setProjects] = useState<any[]>([]);
  const [clusters, setClusters] = useState<Array<{value: string, label: string}>>([]);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedClusterName, setSelectedClusterName] = useState('');
  const [applications, setApplications] = useState<string[]>([]);
  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [applications]);
  const sortedFuelOptions = useMemo(() => {
    return [
      { value: 'electric', label: 'Electric' },
      { value: 'gas', label: 'Gas' },
      { value: 'hybrid', label: 'Water' },
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [modalFormData, setModalFormData] = useState({
    level: 'Pilot',
    application: '',
    fuelType: '',
    restricted: 'All',
    configurations: `[
  {
    "configKey": "example.key.one",
    "displayName": "Example One",
    "value": "true"
  },
  {
    "configKey": "example.key.two",
    "displayName": "Example Two",
    "value": "123"
  }
]`
  });
  const [addClusterId, setAddClusterId] = useState<string>('');

  // Get the selected pilot's projectId (string) for API call
  const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
  const pilotIdForApi = selectedPilot?.projectId || '';

  const fetchConfigs = useCallback(async () => {
    if (!pilotIdForApi) return;

    setIsLoading(true);
    setError(null);

    try {
      let response;

      if (levelFilter === 'cluster' && selectedCluster) {
        response = await ClusterApis.getClusterConfigs(rc, selectedCluster, pilotIdForApi);
      } else if (applicationFilter || (fuelTypeFilter && fuelTypeFilter !== 'All')) {
        response = await PilotApis.getPilotConfigsWithFilters(
          rc,
          pilotIdForApi,
          selectedPilot?.api_host || '',
          applicationFilter,
          fuelTypeFilter
        );
      } else {
        response = await PilotApis.getIngestionConfigs(rc, pilotIdForApi);
      }

      if (response.success && response.data) {
        const validConfigs = Array.isArray(response.data) ? response.data.filter(cfg => 
          cfg && typeof cfg === 'object' && cfg.id && cfg.configKey
        ) : [];

        if (validConfigs.length > 0) {
          setConfigs(validConfigs);
          setLastSaved(validConfigs.map(cfg => cfg.defaultValue || ''));
          setPage(1);
        } else {
          setError('No valid configurations found in response');
          setConfigs([]);
          setLastSaved([]);
          setPage(1);
        }
      } else {
        setError(response.error || 'Failed to fetch configurations');
        setConfigs([]);
        setLastSaved([]);
        setPage(1);
      }
    } catch (err) {
      setError('Failed to fetch configurations');
      setConfigs([]);
      setLastSaved([]);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  }, [rc, pilotIdForApi, levelFilter, selectedCluster, selectedPilot?.api_host, applicationFilter, fuelTypeFilter]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);


  const filteredConfigs = useMemo(() => {
    // Ensure configs is an array
    if (!Array.isArray(configs)) {
      return [];
    }
    
        let filtered = configs;

    
    // Apply search filter
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(cfg => {
        // Safety check for each config object
        if (!cfg || typeof cfg !== 'object') {
          return false;
        }
        
        return (
          (cfg.configKey && cfg.configKey.toLowerCase().includes(s)) ||
          (cfg.displayName && cfg.displayName.toLowerCase().includes(s)) ||
          (cfg.configType && cfg.configType.toLowerCase().includes(s)) ||
          (cfg.configDocumentation && cfg.configDocumentation.toLowerCase().includes(s)) ||
          (cfg.configLevel && cfg.configLevel.toLowerCase().includes(s)) ||
          (cfg.configDataType && cfg.configDataType.toLowerCase().includes(s)) ||
          (cfg.features && cfg.features.toLowerCase().includes(s)) ||
          (cfg.defaultValue && typeof cfg.defaultValue === 'string' && cfg.defaultValue.toLowerCase().includes(s)) ||
          (cfg.possibleValues && typeof cfg.possibleValues === 'string' && cfg.possibleValues.toLowerCase().includes(s)) ||
          (cfg.configRegex && cfg.configRegex.toLowerCase().includes(s)) ||
          (cfg.id && cfg.id.toLowerCase().includes(s)) ||
          (cfg.source && cfg.source.toLowerCase().includes(s)) ||
          (cfg.relatedConfigs && typeof cfg.relatedConfigs === 'string' && cfg.relatedConfigs.toLowerCase().includes(s))
        );
      });
    }
    
    return filtered;
  }, [configs, search, levelFilter, applicationFilter, fuelTypeFilter, restrictedFilter, selectedCluster]);

  const paginatedConfigs = useMemo(() => {
    // Ensure filteredConfigs is an array
    if (!Array.isArray(filteredConfigs)) {
      return [];
    }
    
    const start = (page - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    const paginated = filteredConfigs.slice(start, end);
    
    return paginated;
  }, [filteredConfigs, page, configs.length]);

  const isDirty = configs.some((cfg, i) => {
    if (!cfg.editable) return false;
    const current = (pendingEdits[cfg.id] ?? (cfg.defaultValue || ''));
    const saved = (lastSaved[i] || '');
    return current !== saved;
  });

  const handleValueChange = (id: string, value: string) => {
    setConfigs(cfgs => cfgs.map(cfg => cfg.id === id ? { ...cfg, defaultValue: value } : cfg));
  };

  // Studio-like inline editing handlers
  const handleStartEditing = (cfg: IngestionConfig) => {
    if (!cfg.editable) return;
    setEditingId(cfg.id);
    setEditingValue(String((pendingEdits[cfg.id] ?? cfg.defaultValue) || ''));
  };

  const handleCancelEditing = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleSaveEditing = async () => {
    if (!editingId) return;

    // Find the target config to construct payload
    const target = configs.find(cfg => cfg.id === editingId);
    if (!target) {
      setEditingId(null);
      setEditingValue('');
      return;
    }

    try {
      const configData = [{
        project_id: pilotIdForApi,
        config_key: target.configKey,
        config_type: target.configType,
        config_value: String(editingValue ?? ''),
        config_level: target.configLevel || 'pilot',
        config_documentation: target.configDocumentation || '',
        config_regex: target.configRegex || '.*',
        config_data_type: target.configDataType || '',
        display_name: target.displayName,
        features: target.features || 'ingestion',
        editable: target.editable,
        is_required: true
      }];

      const result = await PilotApis.saveProjectConfigs(rc, configData as any);
      if (result.success) {
        // Update local state with saved value
        setConfigs(cfgs => cfgs.map(cfg => cfg.id === editingId ? { ...cfg, defaultValue: editingValue } : cfg));
        setLastSaved(prev => {
          const next = [...prev];
          const idx = configs.findIndex(cfg => cfg.id === editingId);
          if (idx !== -1) next[idx] = String(editingValue ?? '');
          return next;
        });
        // Clear pending edit for this id since it is saved
        setPendingEdits(prev => {
          const next = { ...prev };
          delete next[editingId];
          return next;
        });
        // (backend-driven dialog now; no slice to update)
        // Highlight the saved row temporarily
        setSavedHighlightIds(prev => Array.from(new Set([...prev, editingId])));
      } else {
        setSaveError(`Failed to save configuration: ${result.error || 'Unknown error'}`);
      }
    } catch (e) {
      setSaveError('Failed to save configuration. Please try again.');
    } finally {
      setEditingId(null);
      setEditingValue('');
    }
  };

  // Action column: open override dialog
  const handleOpenOverrideDialog = async (cfg: IngestionConfig) => {
    setOverrideConfig(cfg);
    setOverrideLevel('pilot');
    setOverrideValue(String(cfg.defaultValue || ''));
    setOverrideOpen(true);

    try {
      // Ensure clusters are available for cluster overrides
      if (clusters.length === 0) {
        const loaded = await fetchClusters();
        const defaultClusterId = selectedCluster || (loaded[0]?.value || '');
        setOverrideCluster(defaultClusterId);
      } else {
        setOverrideCluster(selectedCluster || (clusters[0]?.value || ''));
      }
    } catch (e) {
      // ignore
      setOverrideCluster('');
    }
  };

  const handleCloseOverrideDialog = () => {
    setOverrideOpen(false);
    setOverrideConfig(null);
  };

  const handleSubmitOverride = async () => {
    if (!overrideConfig) return;

    if (overrideLevel === 'cluster' && !overrideCluster) {
      alert('Please select a cluster to create a cluster-level override.');
      return;
    }

    setOverrideSaving(true);
    try {
      const configData = [{
        project_id: pilotIdForApi,
        config_key: overrideConfig.configKey,
        config_type: overrideConfig.configType,
        config_value: overrideValue,
        config_level: overrideLevel,
        config_documentation: overrideConfig.configDocumentation || '',
        config_regex: overrideConfig.configRegex || '.*',
        config_data_type: overrideConfig.configDataType || '',
        display_name: overrideConfig.displayName,
        features: overrideConfig.features || 'ingestion',
        editable: true,
        is_required: true,
        cluster_id: overrideLevel === 'cluster' ? overrideCluster : undefined
      }];

      const result = await PilotApis.saveProjectConfigs(rc, configData as any);
      if (result.success) {
        await fetchConfigs();
        handleCloseOverrideDialog();
      } else {
        alert('Failed to add override: ' + (result.error || 'Unknown error'));
      }
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleValueKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSaveEditing();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEditing();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Get only the configs that have been modified and are editable
      const modifiedConfigs = configs.filter((cfg, index) => {
        if (!cfg.editable) return false;
        const currentVal = (pendingEdits[cfg.id] ?? (cfg.defaultValue || ''));
        const savedVal = (lastSaved[index] || '');
        return currentVal !== savedVal;
      });
      
      if (modifiedConfigs.length === 0) {
        alert('No changes to save!');
        return;
      }
      
      // Prepare array of config objects for single API call
      const configsData = modifiedConfigs.map(cfg => ({
        project_id: pilotIdForApi,
        config_key: cfg.configKey,
        config_type: cfg.configType,
        config_value: ((pendingEdits[cfg.id] ?? cfg.defaultValue) || ''),
        config_level: cfg.configLevel || 'pilot',
        config_documentation: cfg.configDocumentation || '',
        config_regex: cfg.configRegex || '.*',
        config_data_type: cfg.configDataType || '',
        display_name: cfg.displayName,
        features: (cfg as any).component || 'ingestion',
        editable: cfg.editable,
        is_required: true
      }));
      
      // Make single API call with array of configs
      const result = await PilotApis.saveProjectConfigs(rc, configsData);
      
      if (result.success) {
        await fetchConfigs();
        // Clear pending edits for saved ids and mark them highlighted
        setPendingEdits(prev => {
          const next = { ...prev };
          for (const cfg of modifiedConfigs) {
            delete next[cfg.id];
          }
          return next;
        });
        setSavedHighlightIds(prev => Array.from(new Set([...prev, ...modifiedConfigs.map(c => c.id)])));
        // (backend-driven dialog now; no slice to update)
        alert(`Successfully saved ${modifiedConfigs.length} configuration(s)!`);
      } else {
        setSaveError(`Failed to save configurations: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setSaveError('Failed to save configurations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatRelatedConfigs = (relatedConfigs: any): string | React.ReactElement => {
    if (!relatedConfigs) return 'N/A';
    
    if (typeof relatedConfigs === 'string') {
      return relatedConfigs || 'N/A';
    }
    
    if (Array.isArray(relatedConfigs)) {
      return relatedConfigs.map(config => 
        typeof config === 'object' && config.configKey 
          ? `${config.configKey} (${config.configType})`
          : String(config)
      ).join(', ') || 'N/A';
    }
    
    if (typeof relatedConfigs === 'object' && relatedConfigs.configKey) {
      return (
        <span>
          <b>configType:</b> {relatedConfigs.configType} <b>configKey:</b> {relatedConfigs.configKey} 
        </span>
      );
    }
    
    return 'N/A';
  };

  const handlePageChange = (_: any, value: number) => setPage(value);

  const handleResetFilters = () => {
    setLevelFilter('pilot');
    setApplicationFilter('');
    setFuelTypeFilter('All');
    setRestrictedFilter('');
    setSearch('');
    setSelectedCluster('');
    setSelectedClusterName('');
    setPage(1);
    // The useEffect will automatically fetch pilot configs when filters are reset
  };

  const handleOpenModal = () => {
    // Set default values based on current filter selections
    setModalFormData({
      level: 'Pilot',
      application: applicationFilter || '',
      fuelType: fuelTypeFilter === 'All' ? '' : fuelTypeFilter.toUpperCase(),
      restricted: 'All',
      configurations: `{
  "configType": "your_config_type",
  "configKVs": [{
    "configKey": "your_config_key",
    "configVal": "your_config_value",
    "configDocumentation": "your documentation",
    "configRegex": ".*",
    "configDataType": "JSON"
  }]
}`
    });
    setAddClusterId(selectedCluster || (clusters[0]?.value || ''));
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleModalFormChange = (field: string, value: string) => {
    setModalFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear JSON error when configurations field changes and becomes valid
    if (field === 'configurations' && jsonError) {
      try {
        JSON.parse(value);
        setJsonError('');
      } catch (error) {
        // JSON is still invalid, keep the error
      }
    }
  };

  const validateJson = (jsonString: string): boolean => {
    try {
      const parsed = JSON.parse(jsonString);
      
      // Check if it's the new format with configType and configKVs
      if (parsed && typeof parsed === 'object' && 'configType' in parsed && 'configKVs' in parsed) {
        if (!Array.isArray(parsed.configKVs)) return false;
        return parsed.configKVs.every((item: any) => 
          item && typeof item === 'object' && 
          'configKey' in item && 'configVal' in item
        );
      }
      
      // Check if it's the old format (array of objects)
      if (Array.isArray(parsed)) {
        return parsed.every((item) => item && typeof item === 'object' && 'configKey' in item && 'displayName' in item && 'value' in item);
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleAddConfigurations = async () => {
    if (!validateJson(modalFormData.configurations)) {
      setJsonError('Invalid JSON. Expect either an array of objects with configKey, displayName, and value, or an object with configType and configKVs array.');
      return;
    }

    if (modalFormData.level === 'Cluster' && !addClusterId) {
      alert('Please select a cluster.');
      return;
    }

    try {
      const parsed = JSON.parse(modalFormData.configurations);
      const featuresStr = [modalFormData.application, modalFormData.fuelType].filter(Boolean).join(', ');
      let configsData: any[] = [];

      // Handle new format with configType and configKVs
      if (parsed && typeof parsed === 'object' && 'configType' in parsed && 'configKVs' in parsed) {
        configsData = parsed.configKVs.map((item: any) => ({
          project_id: selectedPilot?.projectId || '',
          config_key: item.configKey,
          config_type: parsed.configType,
          config_value: String(item.configVal ?? ''),
          config_level: modalFormData.level === 'Cluster' ? 'cluster' : 'pilot',
          config_documentation: item.configDocumentation || '',
          config_regex: item.configRegex || '.*',
          config_data_type: item.configDataType || 'TEXT',
          display_name: item.configKey, // Use configKey as display name if not provided
          features: featuresStr || 'ingestion',
          editable: true,
          is_required: true,
          cluster_id: modalFormData.level === 'Cluster' ? addClusterId : undefined
        }));
      } 
      // Handle old format (array of objects)
      else if (Array.isArray(parsed)) {
        configsData = parsed.map((item: any) => ({
          project_id: selectedPilot?.projectId || '',
          config_key: item.configKey,
          config_type: 'CUSTOM',
          config_value: String(item.value ?? ''),
          config_level: modalFormData.level === 'Cluster' ? 'cluster' : 'pilot',
          config_documentation: '',
          config_regex: '.*',
          config_data_type: 'TEXT',
          display_name: item.displayName,
          features: featuresStr || 'ingestion',
          editable: true,
          is_required: true,
          cluster_id: modalFormData.level === 'Cluster' ? addClusterId : undefined
        }));
      }

      setJsonError('');
      setModalLoading(true);

      const result = await PilotApis.saveProjectConfigs(rc, configsData as any);

      if (result.success) {
        await fetchConfigs();
        alert('Configuration(s) added successfully');
        handleCloseModal();
      } else {
        alert('Error adding configuration: ' + (result.error || 'Unknown error'));
      }
    } catch {
      setJsonError('Invalid JSON format');
    } finally {
      setModalLoading(false);
    }
  };

  // create a method to fetch applications
   
  const fetchApplications = async () => {
    try {
      if (!selectedPilot || !selectedPilot.projectId) {
        return;
      }
      
      const response = await PilotApis.getApplicationsMetadata(rc, selectedPilot.api_host||'', selectedPilot.projectId);
      
      if (response.success && response.data) {
        if (response.data.communication_events && Array.isArray(response.data.communication_events)) {
          setApplications(response.data.communication_events);
        } else {
          setApplications([]);
        }
      } else {
        setApplications([]);
      }
      
    } catch (error) {
      setApplications([]);
    }
  };
  
  useEffect(() => {
    fetchApplications();
  }, [selectedPilot]);

  const fetchClusters = async (): Promise<Array<{value: string, label: string}>> => {
    try {
      if (!selectedPilot || !selectedPilot.projectId) {
        return [];
      }
      
      setClusterLoading(true);

      const response = await ClusterApis.getClusters(rc, selectedPilot.projectId);
      if (response.success && response.data) {
        const combinedClusters = response.data.combined_clusters || [];
        // Transform the API response to match the expected format: {value: string, label: string}[]
        const loaded: Array<{value: string, label: string}> = combinedClusters
          .map((cluster: any) => ({
            value: cluster.clusterReference || cluster.value || cluster.id || '',
            label: cluster.label || cluster.clusterReference || cluster.name || 'Unknown Cluster'
          }))
          .filter((c): c is {value: string, label: string} => 
            Boolean(c.value) && c.value !== 'DEFAULT' && Boolean(c.label)
          );
        setClusters(loaded);
        return loaded;
      } else {
        setClusters([]);
        return [];
      }
    } catch (error) {
      setClusters([]);
      return [];
    } finally {
      setClusterLoading(false);
    }
  };

  // Fetch clusters when level filter changes to cluster
  useEffect(() => {
    if (levelFilter === 'cluster') {
      fetchClusters();
    } else {
      setSelectedCluster('');
      setClusters([]);
    }
  }, [levelFilter, selectedPilotId]);

  return (
    <Box sx={{ 
      bgcolor: theme.palette.background.default, 
      minHeight: '100vh', 
      py: 4,
      position: 'relative'
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        <Card sx={{ 
          p: 0, 
          borderRadius: 3, 
          boxShadow: 3,
          // Let theme Paper background apply
         }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4, pt: 4 }}>
            <Box sx={{ display: 'flex', flexDirection:"column" }}>
                <Box sx={{display:"flex",flexDirection:"row"}}>
                  <ChecklistIcon color="primary" sx={{ fontSize: 38, marginRight: 1 }} />
                  <Typography 
                    variant="h4" 
                    fontWeight={800} 
                    sx={{ 
                      mb: 0.5,
                      textAlign:"left",
                      color: theme.palette.text.primary
                    }}
                  >
                    {levelFilter === 'cluster' && selectedCluster ? 'Cluster Configs' : 'Project Configs'}
                  </Typography>
                </Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: theme.palette.text.secondary
                  }}
                >
                  Browse, edit, or add {levelFilter === 'cluster' && selectedCluster ? `cluster-specific` : 'project-specific'} configurations. 
                  {levelFilter === 'cluster' && selectedCluster ? ` Cluster: ${selectedClusterName||selectedCluster} ` : ''} 
                  Displaying {filteredConfigs.length} of {configs.length} configurations.
                </Typography>
            </Box>
            <Box sx={{display:"flex",flexDirection:"row"}}>
              <BglPrimaryOutlinedButton
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutlineIcon />}
                // size="large"
                variant="outlined"
                color="secondary"
                onClick={handleOpenModal}
                sx={{
                  marginRight: 1,
                  textTransform: 'capitalize'
                }}
              >
                {'Add New Config'}
              </BglPrimaryOutlinedButton>
              <BglPrimaryButton
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                size="large"
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                sx={{
                   textTransform: 'capitalize'
                }}
              >
                {isSaving ? 'Saving...' : 'Save All Updates'}
              </BglPrimaryButton>
            </Box>
          </Box>
           
          <CardContent sx={{ pt: 2, px: 4, position: 'relative', pb: 8 }}>
            {/* Filter Actions */}
           <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mb:2,
              }}>
                <Typography
                  onClick={handleResetFilters}
                  sx={{ 
                    cursor: 'pointer',
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    }
                  }}
                >
                  Reset Filters
                </Typography>
              </Box>
            {/* Filter Section */}
            <Box sx={{ 
              mb: 3, 
              p: 1, 
              pt: 2,
              pb: 2,
              // Use theme surface colors by default
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                alignItems: 'center'
              }}>
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      mb: 1,
                      textAlign:"left"
                    }}
                  >
                    Level
                  </Typography>
                  <BglSelect
                    value={levelFilter}
                    onChange={(v) => {
                      setLevelFilter(String(v));
                      setPage(1);
                      if (v !== 'cluster') setSelectedCluster('');
                    }}
                    options={[{ value: 'pilot', label: 'Pilot' }, { value: 'cluster', label: 'Cluster' }]}
                    placeholder="Level"
                    minWidth={180}
                  />
                </Box>
                
                {/* Cluster dropdown - only show when level is cluster */}
                {levelFilter === 'cluster' && (
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        mb: 1,
                        textAlign:"left"
                      }}
                    >
                      Cluster
                    </Typography>
                    <BglSelect
                      value={selectedCluster}
                      onChange={(v) => {
                        const newClusterId = String(v);
                        setSelectedCluster(newClusterId);
                        const found = clusters.find(c => c.value === newClusterId);
                        const label = found?.label || '';
                        const nameOnly = label.includes(' - ') ? label.split(' - ')[0] : label;
                        setSelectedClusterName(nameOnly);
                        setPage(1);
                      }}
                      options={clusters}
                      placeholder={clusterLoading ? 'Loading clusters…' : 'Select Cluster'}
                      minWidth={180}
                      loading={clusterLoading}
                      sort
                    />
                  </Box>
                )}
                
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      mb: 1,
                      textAlign:"left"
                    }}
                  >
                    Application
                  </Typography>
                  <BglSelect
                    value={applicationFilter}
                    onChange={(v) => { setApplicationFilter(String(v)); setPage(1); }}
                    options={sortedApplications.map(a => ({ value: a, label: a }))}
                    placeholder="Select application"
                    minWidth={180}
                    sort
                  />
                </Box>
                
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      mb: 1,
                      textAlign:"left"
                    }}
                  >
                    Fuel Type
                  </Typography>
                  <BglSelect
                    value={fuelTypeFilter}
                    onChange={(v) => { setFuelTypeFilter(String(v)); setPage(1); }}
                    options={sortedFuelOptions}
                    placeholder="Select fuel type"
                    minWidth={180}
                    sort
                  />
                </Box>
                
                {/* <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      mb: 1,
                      textAlign:"left"
                    }}
                  >
                    Restricted
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={restrictedFilter}
                      onChange={(e) => {
                        setRestrictedFilter(e.target.value);
                        setPage(1);
                      }}
                      displayEmpty
                      sx={{
                        minWidth: '100px',
                        textAlign: 'left',
                        bgcolor: theme.palette.background.paper,
                        borderRadius: 1,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box> */}
              </Box>
            </Box>
            
            <Box sx={{ mb: 2, mt: 1 }}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Search all configurations..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { 
                    bgcolor: theme.palette.background.paper, 
                    borderRadius: 2 
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Box>
            <TableContainer component={Paper} sx={{ 
              maxHeight: 600, 
              borderRadius: 2, 
              boxShadow: 0, 
              mb: 0
            }}>
              <Table stickyHeader size="medium" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ 
                    bgcolor: theme.palette.background.default
                  }}>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary
                    }}>Config Type</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary
                    }}>Config Key</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary
                    }}>Display Name</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary
                    }}>Config Level</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      minWidth: 120,
                      maxWidth: 360
                    }}>Config Value</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      textAlign: 'center'
                    }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedConfigs) && paginatedConfigs.map((cfg, index) => {
                    // Safety check to ensure cfg is a valid object
                    if (!cfg || typeof cfg !== 'object') {
                      return null;
                    }
                    
                    return (
                      <TableRow
                        key={cfg.id || `config-${index}`}
                        sx={{
                          bgcolor: cfg.editable 
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                          ...(savedHighlightIds.includes(cfg.id) ? {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                            boxShadow: `inset 0 0 0 2px ${theme.palette.success.light}`
                          } : {}),
                          ...(cfg.isPushedToDataServer === false ? {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                            boxShadow: `inset 0 0 0 2px ${theme.palette.success.light}`
                          } : {}),
                          ...(() => {
                            const cfgIndex = configs.findIndex(c => c.id === cfg.id);
                            const current = pendingEdits[cfg.id];
                            const savedVal = cfgIndex >= 0 ? (lastSaved[cfgIndex] || '') : String(cfg.defaultValue || '');
                            if (current !== undefined && current !== savedVal && !savedHighlightIds.includes(cfg.id)) {
                              return {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.10)' : 'rgba(255, 213, 79, 0.10)',
                                boxShadow: `inset 0 0 0 2px ${theme.palette.warning.light}`
                              };
                            }
                            return {};
                          })(),
                          '&:hover': cfg.editable 
                            ? { bgcolor: theme.palette.action.hover } 
                            : undefined
                        }}
                      >
                        <TableCell sx={{ 
                          color: theme.palette.text.primary
                        }}>{String(cfg.configType || 'N/A')}</TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.text.primary
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{String(cfg.configKey || 'N/A')}</Typography>
                            {cfg.configDocumentation ? (
                              <Tooltip title={cfg.configDocumentation} placement="top" arrow>
                                <InfoOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary }}
                                />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.text.primary
                        }}>{String(cfg.displayName || 'N/A')}</TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.text.primary
                        }}>
                          <Chip size="small" label={String(cfg.configLevel || 'N/A')} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360, width: 360 }}>
                          {cfg.editable ? (
                            editingId === cfg.id ? (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <TextField
                                  value={editingValue}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    const nextVal = e.target.value;
                                    setEditingValue(nextVal);
                                    setPendingEdits(prev => ({ ...prev, [editingId as string]: nextVal }));
                                  }}
                                  onKeyDown={handleValueKeyDown as React.KeyboardEventHandler<HTMLDivElement>}
                                  autoFocus
                                  multiline
                                  minRows={1}
                                  fullWidth
                                  size="small"
                                   sx={{
                                     maxWidth: 360,
                                     '& .MuiOutlinedInput-root': {
                                       color: theme.palette.text.primary,
                                       borderRadius: 1,
                                       '& fieldset': { borderColor: theme.palette.divider }
                                     },
                                     '& .MuiOutlinedInput-input': {
                                       fontFamily: 'monospace',
                                       fontSize: theme.typography.body2.fontSize
                                     }
                                   }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Tooltip title="Save (Enter)" arrow>
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleSaveEditing}>
                                      <SaveIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel (Esc)" arrow>
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleCancelEditing}>
                                      <CancelIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ) : (
                              <Box 
                                onClick={() => handleStartEditing(cfg)}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  px: 1,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: theme.palette.action.hover },
                                  '&:hover .editIcon': { opacity: 1 }
                                }}
                              >
                                <Tooltip title={String((pendingEdits[cfg.id] ?? cfg.defaultValue) || '')} placement="top" arrow>
                                  <Box sx={{
                                    fontFamily: 'monospace',
                                    fontSize: theme.typography.body2.fontSize,
                                    color: theme.palette.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                   maxWidth: 340
                                  }}>
                                    {String((pendingEdits[cfg.id] ?? cfg.defaultValue) || 'empty')}
                                  </Box>
                                </Tooltip>
                                <IconButton size="small" className="editIcon" sx={{ opacity: 0 }}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          ) : (
                            <Box sx={{ 
                              color: theme.palette.text.disabled, 
                              fontStyle: 'italic', 
                              px: 1,
                             whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}>
                              {String(cfg.defaultValue || 'N/A')}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Tooltip title="Override config at different level" arrow>
                            <IconButton size="small" onClick={() => handleOpenOverrideDialog(cfg)}>
                              <AddCircleOutlineIcon
                                fontSize="small"
                                sx={{ color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary }}
                              />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 5,
              position: 'sticky',
              bottom: 0,
              width: '100%',
              background: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              zIndex: 2
            }}>
              <BglPrimaryOutlinedSmallButton
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Previous
              </BglPrimaryOutlinedSmallButton>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary
                }}
              >
                Page {page} of {Math.max(1, Math.ceil(filteredConfigs.length / ROWS_PER_PAGE))}
              </Typography>
              <BglPrimaryOutlinedSmallButton
                disabled={page >= Math.ceil(filteredConfigs.length / ROWS_PER_PAGE) || filteredConfigs.length === 0}
                onClick={() => setPage(page + 1)}
              >
                Next
              </BglPrimaryOutlinedSmallButton>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Overlay Loader for Operations */}
      <BglOverlayLoader
        open={isLoading || isSaving}
        message={
          isLoading 
            ? levelFilter === 'cluster' && selectedCluster 
              ? `Loading cluster configurations for ${selectedCluster}...` 
              : "Loading configurations..."
            : isSaving 
              ? "Saving configurations..." 
              : ""
        }
        size={50}
      />

      {/* Add New Configurations Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            // bgcolor: theme.palette.background.paper
           }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" fontWeight={600}>
            Add New Configurations
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{ 
              minWidth: 'auto', 
              p: 1,
              color: theme.palette.text.secondary
            }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Create new configurations by pasting JSON. You can use either:<br/>
            • Array format: Objects with <code>configKey</code>, <code>displayName</code>, and <code>value</code><br/>
            • Config format: Object with <code>configType</code> and <code>configKVs</code> array
          </Typography>
          
          {/* Filter Fields */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3, 
            mb: 3 
          }}>
            <Box sx={{ flex: '0 0 auto', width: 180 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 1, textAlign: 'left' }}>Level</Typography>
              <BglSelect
                value={modalFormData.level}
                onChange={async (v) => {
                  const next = String(v);
                  setModalFormData(prev => ({ ...prev, level: next }));
                  if (next === 'Cluster' && clusters.length === 0) {
                    const loaded = await fetchClusters();
                    setAddClusterId(selectedCluster || (loaded[0]?.value || ''));
                  }
                }}
                options={[{ value: 'Pilot', label: 'Pilot' }, { value: 'Cluster', label: 'Cluster' }]}
                placeholder="Level"
                minWidth={180}
              />
            </Box>

            {modalFormData.level === 'Cluster' && (
              <Box sx={{ flex: '0 0 auto', width: 180 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 1, textAlign: 'left' }}>Cluster</Typography>
                <BglSelect
                  value={addClusterId}
                  onChange={(v) => setAddClusterId(String(v))}
                  options={clusters}
                  placeholder={clusterLoading ? 'Loading clusters…' : 'Select cluster'}
                  minWidth={180}
                  loading={clusterLoading}
                  sort
                />
              </Box>
            )}

            <Box sx={{ flex: '0 0 auto', width: 180 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 1, textAlign: 'left' }}>Application</Typography>
              <BglSelect
                value={modalFormData.application}
                onChange={(v) => handleModalFormChange('application', String(v))}
                options={[{ value: 'ALL', label: 'ALL' }, ...sortedApplications.map(a => ({ value: a, label: a }))]}
                placeholder="Select application"
                minWidth={180}
                sort
              />
            </Box>

            <Box sx={{ flex: '0 0 auto', width: 180 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 1, textAlign: 'left' }}>Fuel Type</Typography>
              <BglSelect
                value={modalFormData.fuelType}
                onChange={(v) => handleModalFormChange('fuelType', String(v))}
                options={sortedFuelOptions.map(opt => ({ value: opt.value.toUpperCase(), label: opt.label.toUpperCase() }))}
                placeholder="Select fuel type"
                minWidth={180}
                sort
              />
            </Box>
          </Box>
          
          {/* JSON Configurations Input */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 600,
                mb: 1,
                textAlign: "left"
              }}
            >
              Configurations (JSON format)
            </Typography>
            <TextField
              multiline
              rows={8}
              fullWidth
              value={modalFormData.configurations}
              onChange={(e) => handleModalFormChange('configurations', e.target.value)}
              error={!!jsonError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  // bgcolor: theme.palette.background.paper,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: theme.typography.body2.fontSize,
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
            {jsonError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {jsonError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            size="small"
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              textTransform: 'capitalize'
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleAddConfigurations}
            variant="contained"
            disabled={modalLoading || (modalFormData.level === 'Cluster' && !addClusterId) || !validateJson(modalFormData.configurations)}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              textTransform: 'capitalize',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              }
            }}
          >
            {modalLoading ? 'Adding...' : 'Add Configurations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override Configuration Modal */}
      <Dialog
        open={overrideOpen}
        onClose={handleCloseOverrideDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, bgcolor: theme.palette.background.paper }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {`Override Configuration${levelFilter === 'cluster' && (selectedClusterName || selectedCluster) ? ` — Cluster: ${selectedClusterName || selectedCluster}` : ''}`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Create a new, more specific configuration for the key <b style={{ fontFamily: 'monospace' }}>{overrideConfig?.configKey}</b>.
          </Typography>
          <Box sx={{ p: 2, mb: 2, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Config Key</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{overrideConfig?.configKey}</Typography>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Display Name</Typography>
              <Typography variant="body2">{overrideConfig?.displayName}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>New Level</Typography>
              <BglSelect
                value={overrideLevel}
                onChange={async (v) => {
                  const nextLevel = v as 'pilot' | 'cluster';
                  setOverrideLevel(nextLevel);
                  if (nextLevel === 'cluster' && !overrideCluster) {
                    const loaded = clusters.length === 0 ? await fetchClusters() : clusters;
                    const defaultClusterId = selectedCluster || (loaded[0]?.value || '');
                    setOverrideCluster(defaultClusterId);
                  }
                }}
                options={[{ value: 'pilot', label: 'Pilot' }, { value: 'cluster', label: 'Cluster' }]}
                placeholder="Select level"
                minWidth={180}
                inputPaddingY={0.7}
              />
            </Box>
            {overrideLevel === 'cluster' && (
              <Box sx={{ minWidth: 260, flex: '1 1 260px' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>Cluster</Typography>
                <BglSelect
                  value={overrideCluster}
                  onChange={(v) => setOverrideCluster(String(v))}
                  options={clusters}
                  placeholder={clusterLoading ? 'Loading clusters…' : 'Select cluster'}
                  minWidth={180}
                  loading={clusterLoading}
                  sort
                  inputPaddingY={0.7}
                />
              </Box>
            )}
          </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>New Value</Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={overrideValue}
                onChange={(e) => setOverrideValue(e.target.value)}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Box>
          {/* End DialogContent body */}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button size="small" onClick={handleCloseOverrideDialog} variant="outlined" sx={{ textTransform: 'capitalize' }}>Cancel</Button>
          <Button size="small" onClick={handleSubmitOverride} disabled={overrideSaving || (overrideLevel === 'cluster' && !overrideCluster)} variant="contained" sx={{ textTransform: 'capitalize' }}>
            {overrideSaving ? 'Adding...' : 'Add Config'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 